import { useEffect, useRef } from 'react'
import type maplibregl from 'maplibre-gl'
import { fetchMapConfig } from '@user/store/map/api'
import { Territory } from '@user/store/territory/typings'
import { ownerCountLabel } from '@user/components/HereMap/TerritoryDraw/territoryMetrics'
import {
  ACTIVE_PIN_LAYER,
  CONTACT_PINS_LAYER,
  TERRITORY_FILL_LAYER,
  TERRITORY_SOURCE,
} from './mapLayerIds'
import { whenMapAlive } from './mapSafety'

/** What a click on a territory hands back. */
export type TerritorySelection = {
  territoryId: number
  /** Where the click landed, so the bubble can be anchored there and travel with the map. */
  lngLat: { lng: number; lat: number }
  /**
   * The other territories under the same point, topmost first.
   *
   * Nothing stops a tenant drawing territories that overlap -- the server's containment check
   * returns every match for exactly that reason -- so a click can be inside two at once. The
   * bubble names them rather than silently picking one and letting the user believe the area
   * belongs to a single owner.
   */
  otherTerritoryIds: number[]
}

type Params = {
  map: maplibregl.Map | null
  /**
   * False while drawing. The lasso owns the canvas then: highlighting whatever the pen passes
   * over is noise, and a completed stroke would land as a click on the territory underneath.
   */
  enabled: boolean
  /** Summaries, for the owner count on the hover chip. Names come from the feature itself. */
  territories: Territory[]
  /** The territory whose bubble is open, kept outlined while the cursor is elsewhere. */
  selectedId: number | null
  onSelect: (selection: TerritorySelection | null) => void
}

const CHIP_CLASS = 'territory-hover-chip'

/**
 * The zoom from which the shape itself stops answering a click.
 *
 * Below this the map is a planning view: territories are what is on screen, and individual
 * addresses are not yet distinguishable. Above it the user is working a street, and a polygon
 * covering every address on it must not be what a click finds -- even in the band between here
 * and the add-lead threshold, where nothing else claims the click either. The name on the map
 * stays available the whole way up, from zoom 9.
 */
const TERRITORY_CLICK_MAX_ZOOM = 14

/** Clear of the cursor, below and right, matching where a native tooltip would sit. */
const CHIP_OFFSET = 14

/**
 * The owner count, or nothing at all.
 *
 * A territory with no owners of its own is NOT unowned: one drawn for an assignment rule never
 * carries owners, because the rule decides who gets the lead. The chip used to say "no owner"
 * there, which named a problem that did not exist -- and it is the common case now that rules
 * are how territories get drawn.
 *
 * It cannot say the true thing either, because that means asking which rules point at this
 * territory, and hovering is deliberately free -- no requests, so sweeping the cursor across a
 * dozen territories costs nothing. So it says only what it actually knows. Clicking opens the
 * bubble, which does ask, and which says "No owner assigned" only once it has confirmed that no
 * rule routes the place.
 */
function ownerLabel(territory: Territory | undefined): string {
  if (!territory) return ''

  const label = ownerCountLabel(territory.ownerIds.length)
  return label ? ` · ${label}` : ''
}

/**
 * Hover and click on the territory polygons (WWM-17508).
 *
 * Two deliberately different weights:
 *
 *  - **Hover** costs nothing. It lifts the fill through feature state and shows a chip with the
 *    name and owner count, both of which are already in the store. Sweeping the cursor across
 *    eight territories must not fire eight live aggregate queries, so no numbers appear here.
 *  - **Click** opens the bubble, and only then are the numbers fetched -- once per territory,
 *    then cached in the slice.
 *
 * The chip is a DOM node this hook owns rather than React state, for the same reason the pin
 * layer tracks its hovered id in a closure: a setState per pointer move re-renders the whole
 * map tree. The precedent for hand-built map DOM is useTerritoryLabels, which exists because
 * the HERE raster style has no glyphs endpoint for a symbol layer.
 *
 * Pins always win. A territory covers every pin inside it, so a click is checked against the
 * pin layers first and gives way -- otherwise a tap meant for a house would open a territory,
 * which is the one outcome that would make the polygons feel like an obstruction.
 *
 * **So does adding a lead, and that decides who owns the click: the zoom does.** A plain map
 * click reverse-geocodes an address and offers to add a lead there, but only from
 * `minZoomForAddLead` (16 by default) -- below that the map click does nothing at all, which is
 * exactly where the territory can have it. So:
 *
 *  - zoomed OUT, below TERRITORY_CLICK_MAX_ZOOM: the whole shape is clickable. Nothing else
 *    wants the click, and this is the planning view where territories are what the map is about.
 *  - zoomed IN: the click belongs to the address under it, and the territory's summary opens
 *    from its name on the map instead (useTerritoryLabels).
 *
 * The shape stands down well before add-lead starts, rather than at the handover point: in that
 * band the user is already looking at streets, and a bubble covering the one they are reading
 * is not worth the clicks it would catch. The add-lead threshold is still read, as a floor -- a
 * tenant configured to add leads from further out must keep its click regardless.
 *
 * At any zoom at most one thing happens, and the two routes overlap rather than leaving a gap:
 * names appear from zoom 9.
 */
export function useTerritoryInteraction({
  map,
  enabled,
  territories,
  selectedId,
  onSelect,
}: Params): void {
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  // The zoom from which a map click means "add a lead here". Matches the default in
  // useMapLibreInstance so the two agree before the config lands; the request itself is cached
  // at module scope, so asking for it here costs nothing.
  const minZoomForAddLeadRef = useRef(16)

  useEffect(() => {
    let active = true

    fetchMapConfig().then((config) => {
      if (active && config)
        minZoomForAddLeadRef.current = config.minZoomForAddLead
    })

    return () => {
      active = false
    }
  }, [])

  // Read inside the handlers rather than listed as a dependency: the summaries array is
  // replaced on every fetch, and re-registering the map listeners each time would drop the
  // hover mid-movement.
  const territoriesRef = useRef(territories)
  territoriesRef.current = territories

  useEffect(() => {
    if (!map || !enabled) return

    const container = map.getContainer()

    const chip = document.createElement('div')
    chip.className = CHIP_CLASS
    // Inline, like the territory labels: the chip sits on the map canvas and must not be
    // reachable by, or reach into, the rest of the page's styling.
    chip.style.cssText = [
      'position: absolute',
      'display: none',
      'z-index: 1000',
      'padding: 4px 8px',
      'border-radius: 4px',
      'background: rgba(33, 33, 33, 0.9)',
      'color: #fff',
      'font: 600 12px/1.2 system-ui, sans-serif',
      'white-space: nowrap',
      // The chip follows the cursor, so it must never become the thing under it.
      'pointer-events: none',
      'user-select: none',
    ].join(';')
    container.appendChild(chip)

    let hoveredId: number | null = null

    const setState = (id: number, state: Record<string, boolean>) =>
      whenMapAlive(map, (live) =>
        live.setFeatureState({ source: TERRITORY_SOURCE, id }, state),
      )

    const clearHover = () => {
      if (hoveredId !== null) setState(hoveredId, { hover: false })
      hoveredId = null
      chip.style.display = 'none'
    }

    /** Both of these reach through the map's style, so both can outlive it. */
    const resetCursor = () =>
      whenMapAlive(map, (live) => {
        live.getCanvas().style.cursor = 'default'
      })

    /**
     * A pin under the pointer, if there is one. Queried per event rather than tracked, because
     * the pin layers are owned by another hook and the two must not share mutable state.
     */
    /**
     * Whether a click on the polygon opens its summary at the current zoom. Capped at
     * TERRITORY_CLICK_MAX_ZOOM, and never past the point where a click means "add a lead here",
     * which a tenant can configure lower than that cap.
     */
    const shapeAnswersClick = () =>
      map.getZoom() <
      Math.min(TERRITORY_CLICK_MAX_ZOOM, minZoomForAddLeadRef.current)

    const pinAt = (point: maplibregl.Point) => {
      const layers = [CONTACT_PINS_LAYER, ACTIVE_PIN_LAYER].filter((id) =>
        map.getLayer(id),
      )
      if (!layers.length) return false
      return map.queryRenderedFeatures(point, { layers }).length > 0
    }

    const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
      const feature = e.features?.[0]
      if (!feature) return

      // Over a pin the pin owns the cursor: its own handler has already set the pointer and
      // opened its bubble, and a territory chip over the top would obscure it.
      if (pinAt(e.point)) {
        clearHover()
        return
      }

      const id = Number(feature.id)

      if (id !== hoveredId) {
        if (hoveredId !== null) setState(hoveredId, { hover: false })
        hoveredId = id
        setState(id, { hover: true })

        const territory = territoriesRef.current.find((t) => t.id === id)
        const name = (feature.properties?.name as string) ?? 'Territory'
        chip.textContent = `${name}${ownerLabel(territory)}`
        chip.style.display = 'block'
      }

      // Only where the shape itself answers a click. Zoomed in, the click belongs to the
      // address beneath, and a pointer cursor over the whole polygon would promise otherwise.
      map.getCanvas().style.cursor = shapeAnswersClick() ? 'pointer' : 'default'

      // Kept clear of the right and bottom edges, where a chip pinned to the cursor would
      // otherwise be cut off by the map container.
      const width = chip.offsetWidth
      const height = chip.offsetHeight
      const x = Math.min(
        e.point.x + CHIP_OFFSET,
        container.clientWidth - width - 4,
      )
      const y = Math.min(
        e.point.y + CHIP_OFFSET,
        container.clientHeight - height - 4,
      )
      chip.style.left = `${Math.max(4, x)}px`
      chip.style.top = `${Math.max(4, y)}px`
    }

    const handleMouseLeave = (e: maplibregl.MapLayerMouseEvent) => {
      clearHover()

      // Only if the pointer is not now over a pin: leaving a territory by moving onto one of
      // its own pins would otherwise reset the cursor the pin handler just set.
      if (!e?.point || !pinAt(e.point)) {
        resetCursor()
      }
    }

    /**
     * One handler on the map rather than one per layer, so there is no question of which fires
     * first: this decides between pin, add-lead, territory and empty map in a single pass.
     */
    const handleClick = (e: maplibregl.MapMouseEvent) => {
      // A pin click closes the territory bubble instead of stacking a second one over it.
      if (pinAt(e.point)) {
        onSelectRef.current(null)
        return
      }

      // Close enough to the ground for this click to be about an address -- whether or not
      // add-lead picks it up, and it does from minZoomForAddLead, where its bubble arrives a
      // moment later after the reverse-geocode and a territory opened here would cover it.
      // The name on the map is the way in at this zoom.
      if (!shapeAnswersClick()) {
        onSelectRef.current(null)
        return
      }

      if (!map.getLayer(TERRITORY_FILL_LAYER)) return

      const features = map.queryRenderedFeatures(e.point, {
        layers: [TERRITORY_FILL_LAYER],
      })

      if (!features.length) {
        // Clicking the open map dismisses the bubble, which is how every other overlay here
        // behaves and the gesture people try first.
        onSelectRef.current(null)
        return
      }

      onSelectRef.current({
        territoryId: Number(features[0].id),
        lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
        otherTerritoryIds: features.slice(1).map((f) => Number(f.id)),
      })
    }

    map.on('mousemove', TERRITORY_FILL_LAYER, handleMouseMove)
    map.on('mouseleave', TERRITORY_FILL_LAYER, handleMouseLeave)
    map.on('click', handleClick)

    return () => {
      // off() is safe on a removed map; the feature-state clear inside clearHover is not, and
      // it is guarded. The chip is ours, so it goes either way.
      map.off('mousemove', TERRITORY_FILL_LAYER, handleMouseMove)
      map.off('mouseleave', TERRITORY_FILL_LAYER, handleMouseLeave)
      map.off('click', handleClick)
      clearHover()
      chip.remove()
    }
  }, [map, enabled])

  // Selection is applied separately from hover: it outlives the pointer, and it has to be
  // re-applied after a base-map switch, which recreates the source and takes its feature state
  // with it.
  useEffect(() => {
    if (!map || selectedId === null) return

    const setSelected = (selected: boolean) =>
      whenMapAlive(map, (live) => {
        // The source can also be absent mid style switch, and setFeatureState on a missing
        // source throws rather than no-opping.
        if (!live.getSource(TERRITORY_SOURCE)) return

        live.setFeatureState(
          { source: TERRITORY_SOURCE, id: selectedId },
          { selected },
        )
      })

    const onStyleData = () => setSelected(true)

    setSelected(true)
    map.on('styledata', onStyleData)

    return () => {
      map.off('styledata', onStyleData)
      setSelected(false)
    }
  }, [map, selectedId])
}
